/**
 * Every URL the player website talks to, in one place.
 *
 * Paths are relative to `VITE_API_BASE_URL` (default `/api/v1`). Nothing else
 * in the app may build an API path by string concatenation: when the backend
 * contract changes, this file plus `wire.ts` are the only files that need to
 * change.
 *
 * Source: internal/api-contract.md §2 Site API.
 */

export const endpoints = {
  csrf: () => '/csrf',
  meta: {
    config: () => '/meta/config',
    countries: () => '/meta/countries',
    dictionaries: () => '/meta/dictionaries',
  },
  auth: {
    register: () => '/auth/register',
    registerPassword: () => '/auth/register/password',
    registerComplete: () => '/auth/register/complete',
    login: () => '/auth/login',
    logout: () => '/auth/logout',
    me: () => '/auth/me',
    emailSend: () => '/auth/email/send',
    emailVerify: () => '/auth/email/verify',
    passwordForgot: () => '/auth/password/forgot',
    passwordReset: () => '/auth/password/reset',
    gameTicketBind: () => '/auth/game-ticket/bind',
  },
  captcha: {
    challenge: () => '/captcha/slider/challenge',
    verify: () => '/captcha/slider/verify',
  },
  account: {
    profile: () => '/account/profile',
    password: () => '/account/password',
    email: () => '/account/email',
    emailSend: () => '/account/email/send',
    emailVerify: () => '/account/email/verify',
    avatar: () => '/account/avatar',
    sessions: () => '/account/sessions',
    session: (id: string) => `/account/sessions/${encodeURIComponent(id)}`,
    gameTokens: () => '/account/game-tokens',
    gameToken: (id: number | string) => `/account/game-tokens/${encodeURIComponent(String(id))}`,
    device: () => '/account/device',
    self: () => '/account',
  },
  players: {
    search: () => '/players',
    profile: (publicId: string) => `/players/${encodeURIComponent(publicId)}`,
    achievements: (publicId: string) => `/players/${encodeURIComponent(publicId)}/achievements`,
    speedruns: (publicId: string) => `/players/${encodeURIComponent(publicId)}/speedruns`,
    points: (publicId: string) => `/players/${encodeURIComponent(publicId)}/points`,
    posts: (publicId: string) => `/players/${encodeURIComponent(publicId)}/posts`,
    followers: (publicId: string) => `/players/${encodeURIComponent(publicId)}/followers`,
    following: (publicId: string) => `/players/${encodeURIComponent(publicId)}/following`,
    follow: (publicId: string) => `/players/${encodeURIComponent(publicId)}/follow`,
  },
  achievements: {
    catalog: () => '/achievements',
    detail: (code: string) => `/achievements/${encodeURIComponent(code)}`,
  },
  me: {
    achievements: () => '/me/achievements',
    points: () => '/me/points',
    pointsEntries: () => '/me/points/entries',
    speedruns: () => '/me/speedruns',
    notifications: () => '/me/notifications',
    notificationsUnreadCount: () => '/me/notifications/unread-count',
    notificationRead: (id: number | string) => `/me/notifications/${encodeURIComponent(String(id))}/read`,
    notificationsReadAll: () => '/me/notifications/read-all',
    notification: (id: number | string) => `/me/notifications/${encodeURIComponent(String(id))}`,
  },
  speedruns: {
    options: () => '/speedruns/options',
    submit: () => '/speedruns',
    detail: (id: number | string) => `/speedruns/${encodeURIComponent(String(id))}`,
  },
  leaderboard: {
    points: () => '/leaderboards/points',
    pointsMe: () => '/leaderboards/points/me',
    speedruns: () => '/leaderboards/speedruns',
    speedrunsMe: () => '/leaderboards/speedruns/me',
  },
  community: {
    boards: () => '/community/boards',
    posts: () => '/community/posts',
    post: (id: number | string) => `/community/posts/${encodeURIComponent(String(id))}`,
    comments: (postId: number | string) =>
      `/community/posts/${encodeURIComponent(String(postId))}/comments`,
    comment: (id: number | string) => `/community/comments/${encodeURIComponent(String(id))}`,
    postLike: (id: number | string) => `/community/posts/${encodeURIComponent(String(id))}/like`,
    commentLike: (id: number | string) => `/community/comments/${encodeURIComponent(String(id))}/like`,
    reports: () => '/community/reports',
  },
} as const;
